import './services.css';
import './style-1.css';
// import './style-global.css'; // Commented out as in original

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group'; // Import per animazioni di transizione
import Header from './Header.js';
import MotionGraphics from './MotionGraphicsTopic.js';
import SoundTopic from './SoundTopic.js';
import CodingTopic from './CodingTopic.js';
import WebTopic from './WebTopic.js';
import ContactForm from './ContactForm3_php.js';

import WebApps from './WebApps.js';
import LissajousReactApp from './LissajousReactApp';
import WorkletDrumMachine from './AudioWorklet-DrumMachine/drum-machine'; // Il tuo componente Drum Machine

import LeftSidebar from './LeftSidebar.js';
import { useSpring, animated } from 'react-spring';
import { easings } from '@react-spring/web';
import SidebarMenu from './SidebarMenu.js';
import MaintenancePage from './MaintenancePage.js';
import CookieConsent from "react-cookie-consent";
import Footer from './Footer.js';
import CookiePreferencesModal from './CookiePreferencesModal';

import PrivacyPolicyPage from './PrivacyPolicyPage.js';
import CookiePolicyPage from './CookiePolicyPage.js'; // Assicurati che questo sia il percorso corretto per CookiePolicyPage

const topics = [
    { title: 'Motion Graphics', id: 'graphics' },
    { title: 'Audio', id: 'sound' },
    { title: 'Software', id: 'code' },
    { title: 'Web', 'id': 'web' },
];

const defaultCookiePreferences = {
    essential: true,
    analytics: false,
    marketing: false,
    externalMedia: false,
};

/**
 * Main Application Component (previously Services)
 * Manages global states, routing, and renders main layout.
 */
export default function Services() {
    const location = useLocation();
    const navigate = useNavigate();

    const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

    // activeTopic ora derivato dal path dell'URL
    const activeTopic = location.pathname.split('/').filter(Boolean)[0] || 'none';

    const [isLeftSidebarCollapsed, setIsLeftSidebarCollapsed] = useState(true);
    const [isBackTransitionActive, setIsBackTransitionActive] = useState(false); 
    const [isSidebarMenuOpen, setIsSidebarMenuOpen] = useState(false);
    const [footerHeight, setFooterHeight] = useState(0);    
    
    useEffect(() => {
        // console.log("Altezza del footer:", footerHeight, "px");
    }, [footerHeight]);
    
    // Reset back transition state after animation completes
    useEffect(() => {
        if (isBackTransitionActive && location.pathname === '/') {
            const timer = setTimeout(() => {
                setIsBackTransitionActive(false);
            }, 1200); // Reset after animation duration (900ms + buffer)
            return () => clearTimeout(timer);
        }
    }, [isBackTransitionActive, location.pathname]);    

    const [acceptedCookieCategories, setAcceptedCookieCategories] = useState(() => {
        try {
            const storedPrefs = localStorage.getItem('cookiePreferences');
            return storedPrefs ? JSON.parse(storedPrefs) : defaultCookiePreferences;
        } catch (e) {
            console.error("Failed to parse cookie preferences from localStorage:", e);
            return defaultCookiePreferences;
        }
    });

    const [showConsentBanner, setShowConsentBanner] = useState(() => {
        return !localStorage.getItem('cookiePreferences');
    });

    const hasAcceptedTrackingCookies = acceptedCookieCategories.externalMedia || acceptedCookieCategories.analytics || acceptedCookieCategories.marketing;

    const handleSaveCookiePreferences = useCallback((prefs) => {
        setAcceptedCookieCategories(prefs);
        try {
            localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
            console.log("Cookie preferences saved:", prefs);
        } catch (e) {
            console.error("Failed to save cookie preferences to localStorage:", e);
        }
        setShowConsentBanner(false);
    }, []);

    const handleAcceptAllFromBanner = useCallback(() => {
        const allAccepted = {};
        Object.keys(defaultCookiePreferences).forEach(key => {
            allAccepted[key] = true;
        });
        handleSaveCookiePreferences(allAccepted);
    }, [handleSaveCookiePreferences]);

    const handleRejectAllFromBanner = useCallback(() => {
        const onlyEssential = { ...defaultCookiePreferences, analytics: false, marketing: false, externalMedia: false };
        handleSaveCookiePreferences(onlyEssential);
    }, [handleSaveCookiePreferences]);


    const [isCookiePreferencesModalOpen, setIsCookiePreferencesModal] = useState(false);

    const handleOpenCookiePreferencesModal = useCallback(() => {
        setIsCookiePreferencesModal(true);
        setShowConsentBanner(false);
    }, []);

    const handleCloseCookiePreferencesModal = useCallback(() => {
        setIsCookiePreferencesModal(false);
    }, []);

    const handleTopicClick = useCallback((topicPath) => {
        if (topicPath === 'privacy-policy' || topicPath === 'cookie-policy') {
            setIsCookiePreferencesModal(false);
        }
        setIsBackTransitionActive(false);
        navigate(`/${topicPath}`);
        setIsSidebarMenuOpen(false);
    }, [navigate]);

    const handleBackInitiate = useCallback(() => {
        setIsBackTransitionActive(true);
        navigate('/');
    }, [navigate]);

    const handleBackComplete = useCallback(() => {
        // The useEffect will handle resetting the state automatically
    }, []);

    const toggleLeftSidebarCollapse = useCallback(() => {
        setIsLeftSidebarCollapsed(prev => !prev);
    }, []);

    const openSidebarMenu = useCallback(() => {
        setIsSidebarMenuOpen(true);
    }, []);
    
    const closeSidebarMenu = useCallback(() => {
        setIsSidebarMenuOpen(false);
    }, []);


    const topicsForSidebarMenu = [
        ...topics,
        { separator: true },
        { title: 'Web-Apps', 'id': 'web-apps' },
        // { title: 'Lissajous App', 'id': 'web-apps/lissajous' },
        // { title: 'Drum Machine', 'id': 'web-apps/drum-machine' },
        { separator: true },
        { title: 'Contact Me', id: 'contact' },
        { title: 'Privacy Policy', id: 'privacy-policy' },
        { title: 'Cookie Policy', id: 'cookie-policy' },
    ];
    const filteredTopicsForSidebarMenu = topicsForSidebarMenu.filter(topic => {
        if (topic.id && activeTopic.startsWith(topic.id)) {
            return false;
        }
        return true;
    });

    const headerBackgroundWidth = isLeftSidebarCollapsed ? '60px' : '250px';

    if (isMaintenanceMode) {
        return <MaintenancePage />;
    }

    return (
        // Applicazione della classe bg-web-01 al div con id="page"
        <div id="page" className="bg-web-01" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', position: 'relative', overflowX: 'hidden' }}>
            {/* Elemento per il background giallo fisso dietro l'header e la sidebar */}
            <div id="behind-header"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '70px',
                    width: headerBackgroundWidth,
                    backgroundColor: 'rgb(220, 219, 57)',
                    zIndex: 990,
                    transition: 'width 0.3s ease-in-out',
                }}
            ></div>

            <Header
                isSidebarCollapsed={isLeftSidebarCollapsed}
                activeTopic={activeTopic}
                onBackClick={handleBackInitiate}
                onOpenSidebarMenu={openSidebarMenu}
                isBackTransitionActive={isBackTransitionActive}
                onTopicClick={handleTopicClick}
            />
            
            <div id="mainContentAndSidebar" style={{ display: 'flex', flexGrow: 1, paddingTop: '70px', paddingBottom: `${footerHeight}px` }}>
                <LeftSidebar        
                    isCollapsed={isLeftSidebarCollapsed}
                    onToggleCollapse={toggleLeftSidebarCollapse}
                    footerHeight={footerHeight}
                    onTopicClick={handleTopicClick}
                />

                <div id="main-wrapper"
                    style={{            
                        marginLeft: isLeftSidebarCollapsed ? '70px' : '250px',            
                        transition: 'margin-left 0.3s ease-in-out',
                        flexGrow: 1,
                        position: 'relative',
                        minHeight: `calc(100vh - 70px - ${footerHeight}px)`,
                        overflowY: 'auto',
                        paddingBottom: '20px',
                        boxSizing: 'border-box' // Correzione: 'border-sizing' non è standard, usa 'border-box'
                    }}
                >
                    {/* Utilizza TransitionGroup per animare il cambio di rotta */}
                    <TransitionGroup component={null}> {/* component={null} evita un div extra */}
                        {/* CSSTransition per applicare le classi di animazione */}
                        <CSSTransition
                            key={location.pathname} // La key cambia quando la rotta cambia, innescando la transizione
                            addEndListener={(node, done) => {
                                // Usa 'animationend' dato che usi @keyframes
                                node.addEventListener('animationend', done, false);
                            }}
                            // Definisci le tue @keyframes qui (classi per entrare e uscire)
                            // Useremo 'fade-enter-active' e 'fade-exit-active' per le animazioni
                            classNames={(() => {
                                const isGoingToHome = location.pathname === '/';
                                const shouldUseBackAnimation = isGoingToHome && isBackTransitionActive;
                                
                                console.log('Animation decision:', {
                                    isGoingToHome,
                                    shouldUseBackAnimation,
                                    currentPath: location.pathname,
                                    isBackTransitionActive
                                });
                                
                                return {
                                    enter: shouldUseBackAnimation ? 'fade-enter-home-back-initial' : 'fade-enter',
                                    enterActive: shouldUseBackAnimation ? 'fade-enter-home-back' : 'fade-enter-active',
                                    exit: 'fade-exit',
                                    exitActive: 'fade-exit-active',
                                };
                            })()}
                            // Il timeout deve corrispondere alla durata massima delle tue animazioni CSS.
                            // Se '--transition-time-01' è 0.5s, allora il timeout è 500ms.
                            timeout={{ enter: 1900, exit: 700 }} // L'entrata dura 500ms, l'uscita dura 700ms. Il più lungo forza la sequenzialità.
                        >
                            <Routes location={location}> {/* Passa location per animare al cambio di rotta */}
                                {/* Home Page / Root Route */}
                                <Route path="/" element={
                                    // Questi contenuti avranno già lo stile di posizionamento assoluto
                                    <HomeContent onTopicClick={handleTopicClick} />
                                } />

                                {/* Routes for each Topic */}
                                <Route path="/graphics" element={
                                    <TopicWrapper title="[Motion] Graphics">
                                        <MotionGraphics acceptedCookieCategories={acceptedCookieCategories} />
                                    </TopicWrapper>
                                } />
                                <Route path="/sound" element={
                                    <TopicWrapper title="SOUND & MUSIC">
                                        <SoundTopic acceptedCookieCategories={acceptedCookieCategories} />
                                    </TopicWrapper>
                                } />
                                <Route path="/code" element={
                                    <TopicWrapper title="Software">
                                        <CodingTopic acceptedCookieCategories={acceptedCookieCategories} />
                                    </TopicWrapper>
                                } />
                                <Route path="/web" element={
                                    <TopicWrapper title="WEB DESIGN">
                                        <WebTopic acceptedCookieCategories={acceptedCookieCategories} />
                                    </TopicWrapper>
                                } />
                                <Route path="/contact" element={
                                    <TopicWrapper title="CONTACT ME">
                                        <ContactForm />
                                    </TopicWrapper>
                                } />
                                <Route path="/web-apps" element={
                                    <TopicWrapper title="Web-Apps">
                                        <WebApps />
                                    </TopicWrapper>
                                } />
                                <Route path="/web-apps/lissajous" element={
                                    <TopicWrapper title="Lissajous App">
                                        <LissajousReactApp />
                                    </TopicWrapper>
                                } />
                                <Route path="/web-apps/drum-machine" element={
                                    <TopicWrapper title="Drum Machine">
                                        <WorkletDrumMachine />
                                    </TopicWrapper>
                                } />
                                <Route path="/privacy-policy" element={
                                    <PolicyWrapper title="Privacy Policy">
                                        <PrivacyPolicyPage />
                                    </PolicyWrapper>
                                } />
                                <Route path="/cookie-policy" element={
                                    <PolicyWrapper title="Cookie Policy">
                                        <CookiePolicyPage />
                                    </PolicyWrapper>
                                } />
                            </Routes>
                        </CSSTransition>
                    </TransitionGroup>
                </div>
            </div>
            
            <SidebarMenu
                isOpen={isSidebarMenuOpen}
                onClose={closeSidebarMenu}
                menuItems={topicsForSidebarMenu}
                onTopicChange={handleTopicClick}
            />
            
            {showConsentBanner && (
                <CookieConsent
                    debug={true}
                    location="bottom"
                    buttonText="Accetta Tutto"
                    declineButtonText="Rifiuta Tutto"
                    cookieName="userCookieConsentDecision"
                    style={{ background: "#2B373B", zIndex: 10000 }}
                    buttonStyle={{ color: "#4e503b", fontSize: "13px", background: "#f5e653" }}
                    declineButtonStyle={{ color: "#f5e653", fontSize: "13px", background: "#6e6e6e" }}
                    expiresIn={365}    
                    enableDeclineButton
                    onAccept={() => {
                        handleAcceptAllFromBanner();
                    }}
                    onDecline={() => {
                        handleRejectAllFromBanner();
                    }}
                >
                    Questo sito web utilizza i cookie per migliorare la tua esperienza. Facendo clic su "Accetta Tutto", acconsenti all'uso di TUTTI i cookie. Puoi rifiutare cliccando su "Rifiuta Tutto" o gestire le tue preferenze.
                    {" "}
                    <a onClick={(e) => { e.preventDefault(); handleOpenCookiePreferencesModal(); }} style={{ color: "#f5e653", cursor: 'pointer', textDecoration: 'underline' }}>Gestisci Preferenze</a>.
                    {" "}
                    <a onClick={(e) => { e.preventDefault(); handleTopicClick('privacy-policy'); }} style={{ color: "#f5e653", cursor: 'pointer', textDecoration: 'underline' }}>Leggi la nostra Privacy Policy</a> e    
                    <a onClick={(e) => { e.preventDefault(); handleTopicClick('cookie-policy'); }} style={{ color: "#f5e653", cursor: 'pointer', textDecoration: 'underline' }}> Cookie Policy</a>.
                </CookieConsent>
            )}

            <CookiePreferencesModal
                isOpen={isCookiePreferencesModalOpen}
                onRequestClose={handleCloseCookiePreferencesModal}
                onSavePreferences={handleSaveCookiePreferences}
                initialPreferences={acceptedCookieCategories}
            />

            <Footer
                onOpenCookiePreferencesModal={handleOpenCookiePreferencesModal}
                hasAcceptedTrackingCookies={hasAcceptedTrackingCookies}
                setFooterHeight={setFooterHeight}
                onTopicClick={handleTopicClick}
            />
        </div>
    );
}

/**
 * Component for the Home Page content.
 * Handles its own react-spring animations for initial elements.
 */
function HomeContent({ onTopicClick }) {
    const props1 = useSpring({
        from: { opacity: 0, transform: 'translateY(-70px)' },
        to: { opacity: 1, transform: 'translateY(0px)' },
        config: {
            mass: 1,
            tension: 150,
            friction: 20,
            delay: 10,
            damping: 30
        }
    });

    // Removed unused springProps as it was duplicating the fade-in-left animation
    
    const topicList = topics.map((topic, index) => (
        <animated.div
            className={`polygon-01 topic underline ${topic.id} fade-in-left`}
            key={topic.id}
            onClick={() => onTopicClick(topic.id)}
            style={{ '--delay-index': index }}
        >
            <h2 className="">{topic.title}</h2>
        </animated.div>
    ));

    return (
        <div 
            id="container-01" 
            className="main-content-home" 
            style={{ 
                position: 'absolute', left: '50%', transform: 'translateX(-50%)', 
                width: '100%', minHeight: '100%', boxSizing: 'border-box', top: 0, zIndex: 2 
            }}
        >
            <div className="heading text-style-01" style={{ fontSize: "3vw", position: "relative" }}>
                <animated.h2 className="heading-title elementor-size-default" style={props1}>
                    DESIGNING...
                </animated.h2>
            </div>

            <div className="grid topics-container child con">
                {topicList}
            </div>
        </div>
    );
}

/**
 * Wrapper component for topic pages to maintain consistent structure and title.
 * CSSTransition in Services.js handles the animations.
 * @param {string} props.title - The title to display for the topic.
 * @param {React.ReactNode} props.children - The actual topic component to render.
 */
function TopicWrapper({ title, children }) {
    return (
        <div 
            id={`topic-chosen-container-${title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`} 
            className={`container-transition`} 
            style={{
                position: 'absolute', left: '50%', transform: 'translateX(-50%)', 
                width: '100%', minHeight: '100%', boxSizing: 'border-box', top: 0, zIndex: 2 
            }}
        >
            <div className="heading">
                <h2 className="title text-style-04">{title}</h2>
                {children}
            </div>
        </div>
    );
}

/**
 * Wrapper component specifically for Policy pages to maintain consistent structure.
 * CSSTransition in Services.js handles the animations.
 * @param {string} props.title - The title for the policy page.
 * @param {React.ReactNode} props.children - The actual policy component to render.
 */
function PolicyWrapper({ title, children }) {
    return (
        <div 
            id={`policy-chosen-container-${title.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`} 
            className={`policy container-transition`}
            style={{
                position: 'absolute', left: '50%', transform: 'translateX(-50%)', 
                width: '100%', minHeight: '100%', boxSizing: 'border-box', top: 0, zIndex: 2 
            }}
        >
            <h2 className="title text-style-04" style={{ textAlign: 'center', marginBottom: '1.5rem' }}>{title}</h2>
            {children}
        </div>
    );
}
